/* ============================================================
 * World Map Social Proof — world-map.js
 * ------------------------------------------------------------
 * Vanilla JS, no dependencies. Initializes every .wm-section.
 * Config is read from data attributes (no Liquid inside JS):
 *   data-tour-delay      seconds before idle tour starts
 *   data-tour-enabled    "true" | "false"
 * Counter config lives on [data-wm-counter]:
 *   data-base, data-tick-min, data-tick-max, data-suffix
 * ============================================================ */
(function () {
  'use strict';

  function initSection(section) {
    var svg = section.querySelector('.wm-map');
    var tooltip = section.querySelector('.wm-tooltip');
    var pillAnchor = section.querySelector('.wm-pill-anchor');
    var pill = section.querySelector('.wm-pill');
    var pingLayer = section.querySelector('.wm-ping-layer');
    var mapWrap = section.querySelector('.wm-map-wrap');
    if (!svg || !pill || !pingLayer || !mapWrap) return;

    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    /* ---------- Data ---------- */
    var coords = { cities: {}, countries: {} };
    var counts = {};
    try {
      coords = JSON.parse(section.querySelector('[data-wm-coords]').textContent);
    } catch (e) { /* missing coords: pings disabled, rest still works */ }
    try {
      counts = JSON.parse(section.querySelector('[data-wm-country-counts]').textContent);
    } catch (e) { /* no counts: tooltips show name only */ }

    /* ---------- Helpers ---------- */
    function vbToPx(x, y) {
      var r = svg.getBoundingClientRect();
      return { x: (x / 700) * r.width, y: (y / 400) * r.height };
    }

    function lookup(cityRaw, countryRaw) {
      var city = (cityRaw || '').trim().toLowerCase();
      var country = (countryRaw || '').trim().toLowerCase();
      if (city && coords.cities[city]) return coords.cities[city];
      if (country && coords.countries[country]) return coords.countries[country];
      // US states / CA provinces / AU states — popups often say
      // "Portland, Oregon" rather than "Portland, US"
      if (country && coords.states && coords.states[country]) return coords.states[country];
      if (country && coords.stateCodes && coords.stateCodes[country]) return coords.stateCodes[country];
      return null;
    }

    function countryGroup(codeOrName) {
      var k = (codeOrName || '').trim();
      var g = svg.querySelector('.wm-country[data-code="' + k.toUpperCase() + '"]');
      if (g) return g;
      var kl = k.toLowerCase();
      var all = svg.querySelectorAll('.wm-country');
      for (var i = 0; i < all.length; i++) {
        if (all[i].dataset.name.toLowerCase() === kl) return all[i];
      }
      return null;
    }

    /* ---------- Tooltip + focus ---------- */
    var pinnedGroup = null;
    var TOOLTIPS = section.dataset.tooltips === 'true';

    function showTooltip(group, px, py) {
      if (!TOOLTIPS) return;
      var code = group.dataset.code.toLowerCase();
      var count = counts[code];
      tooltip.textContent = group.dataset.name + (count ? ' \u00b7 ' + count : '');
      tooltip.hidden = false;
      var wrapR = mapWrap.getBoundingClientRect();
      var tw = tooltip.offsetWidth;
      var th = tooltip.offsetHeight;
      var x = Math.min(Math.max(px + 12, 4), wrapR.width - tw - 4);
      var y = Math.min(Math.max(py - th - 10, 4), wrapR.height - th - 4);
      tooltip.style.left = x + 'px';
      tooltip.style.top = y + 'px';
    }

    function hideTooltip() { tooltip.hidden = true; }

    function focusCountry(group) {
      svg.querySelectorAll('.wm-country.is-focus').forEach(function (g) {
        g.classList.remove('is-focus');
      });
      if (group) {
        group.classList.add('is-focus');
        svg.setAttribute('data-focus', '');
      } else {
        svg.removeAttribute('data-focus');
      }
    }

    function groupCenterTop(g) {
      var hit = g.querySelector('.wm-hit');
      var cx = parseFloat(hit.getAttribute('x')) + parseFloat(hit.getAttribute('width')) / 2;
      var cy = parseFloat(hit.getAttribute('y'));
      return vbToPx(cx, cy);
    }

    /* ---------- Hover (delegated, desktop) ---------- */
    svg.addEventListener('pointerover', function (e) {
      if (pinnedGroup) return;
      var g = e.target.closest('.wm-country--active');
      if (!g) return;
      markInteraction();
      focusCountry(g);
    });

    svg.addEventListener('pointermove', function (e) {
      if (pinnedGroup) return;
      var g = e.target.closest('.wm-country--active');
      if (!g) return;
      var wrapR = mapWrap.getBoundingClientRect();
      showTooltip(g, e.clientX - wrapR.left, e.clientY - wrapR.top);
    });

    svg.addEventListener('pointerout', function (e) {
      if (pinnedGroup) return;
      if (!e.relatedTarget || !e.relatedTarget.closest('.wm-country--active')) {
        focusCountry(null);
        hideTooltip();
      }
    });

    /* ---------- Tap (mobile): pinned tooltip toggle ---------- */
    svg.addEventListener('click', function (e) {
      var g = e.target.closest('.wm-country--active');
      markInteraction();
      if (!g || g === pinnedGroup) {
        pinnedGroup = null;
        focusCountry(null);
        hideTooltip();
        return;
      }
      pinnedGroup = g;
      focusCountry(g);
      var p = groupCenterTop(g);
      showTooltip(g, p.x, p.y);
    });

    document.addEventListener('click', function (e) {
      if (pinnedGroup && !section.contains(e.target)) {
        pinnedGroup = null;
        focusCountry(null);
        hideTooltip();
      }
    });

    /* ---------- Keyboard ---------- */
    svg.addEventListener('keydown', function (e) {
      var g = e.target.closest('.wm-country--active');
      if (!g) return;
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        g.click();
      }
      if (e.key === 'Escape') {
        pinnedGroup = null;
        focusCountry(null);
        hideTooltip();
      }
    });

    /* ---------- Ping + pill ---------- */
    function flashCountry(codeOrName) {
      var g = countryGroup(codeOrName);
      if (!g) return;
      g.classList.add('is-flash');
      setTimeout(function () { g.classList.remove('is-flash'); }, 900);
    }

    var lastVb = null;

   function positionPill(vb) {
      var p = vbToPx(vb[0], vb[1]);
      var wrapR = mapWrap.getBoundingClientRect();
      var pw = pillAnchor.offsetWidth || 160;
      var ph = pillAnchor.offsetHeight || 32;

      pillAnchor.style.maxWidth = (wrapR.width - 8) + 'px';
      pw = Math.min(pw, wrapR.width - 8);

      var left = p.x + 8;
      var top = Math.min(Math.max(p.y - (ph / 2), 4), wrapR.height - ph - 4);

      if (left + pw > wrapR.width - 4) {
        left = p.x - pw - 8; 
      }

      if (left < 4) {
        left = (wrapR.width - pw) / 2;
        
        top = p.y + 12; 
        
        if (top + ph > wrapR.height) {
          top = p.y - ph - 12; 
        }
      }

      // Apply the final positions
      pillAnchor.style.left = Math.max(left, 4) + 'px';
      pillAnchor.style.top = Math.max(top, 4) + 'px';
      pillAnchor.classList.add('is-visible');
    }

    function firePing(vb, countryLabel) {
      lastVb = vb;
      pingLayer.setAttribute('transform', 'translate(' + vb[0] + ',' + vb[1] + ')');
      pingLayer.classList.remove('is-live');
      void pingLayer.getBoundingClientRect(); // force reflow to restart animation
      pingLayer.classList.add('is-live');

      requestAnimationFrame(function () { positionPill(vb); });

      if (countryLabel) flashCountry(countryLabel);
    }

    /* ---------- ProveSource inline div = primary event stream ---------- */
    var lastFire = 0;
    var gotInlineEvent = false;
    var productEl = section.querySelector('[data-wm-pill-product]');
    var locEl = section.querySelector('[data-wm-pill-location]');
    var defaultBrand = productEl ? productEl.textContent.trim() : '';

    /* Product display names: raw title (lowercase) -> short name.
     * Built by Liquid from custom.short_product_name metafield with
     * product title fallback. Unknown titles display as-is. */
    var productNames = {};
    try {
      productNames = JSON.parse(
        section.querySelector('[data-wm-product-names]').textContent
      );
    } catch (e) { /* map missing: raw titles display unchanged */ }

    var USE_SHORT_NAMES = section.dataset.useShortNames !== 'false';

    function displayName(product) {
      if (!product) return defaultBrand;
      if (!USE_SHORT_NAMES) return product;
      return productNames[product.toLowerCase()] || product;
    }

    function renderPill(product, city, country) {
      if (productEl) {
        var finalName = displayName(product);
        productEl.textContent = finalName.split(/[-–—]/)[0].trim();
      }
      if (locEl) locEl.textContent = city + (country ? ', ' + country : '');
    }

    /* ---------- Event cache: replay real events if the feed stalls ---------
     * ProveSource's inline loop can stop cycling after a while. Every real
     * event we successfully display is cached (deduped, max 30); if no new
     * event arrives within STALE_MS, we replay cached events round-robin so
     * the map stays alive with genuinely real recent orders. A fresh real
     * event always takes over again (it resets the staleness clock).
     * ---------------------------------------------------------------------- */
    var seenEvents = [];
    var seenKeys = {};
    var seenIdx = 0;
    var STALE_MS = (parseFloat(section.dataset.staleAfter) || 30) * 1000;

    function cacheEvent(city, country, product) {
      var key = (city + '|' + country + '|' + product).toLowerCase();
      if (seenKeys[key]) return;
      seenKeys[key] = true;
      seenEvents.push({ city: city, country: country, product: product });
      if (seenEvents.length > 30) {
        var removed = seenEvents.shift();
        delete seenKeys[(removed.city + '|' + removed.country + '|' + removed.product).toLowerCase()];
      }
    }

    function handleEvent(city, country, product, isReplay) {
      var vb = lookup(city, country);
      if (!vb) return false; // unknown location: skip gracefully
      var now = Date.now();
      if (now - lastFire < 500) return false; // debounce
      lastFire = now;
      tourPause(); // real events take priority over idle tour
      renderPill(product, city, country);
      firePing(vb, country);
      if (!isReplay) cacheEvent(city, country, product);
      return true;
    }

    function startStaleWatchdog() {
      setInterval(function () {
        if (Date.now() - lastFire < STALE_MS) return;
        if (!seenEvents.length) return;
        var e = seenEvents[seenIdx % seenEvents.length];
        seenIdx += 1;
        handleEvent(e.city, e.country, e.product, true);
      }, 5000);
    }

    /* Expected pill text: "Product | City, CC" (product optional).
     * The product part may contain multiple ~-separated candidates from
     * different template variables (productName, product.name,
     * products.0.name) — take the first that actually populated.
     * Unreplaced placeholders (containing %) are treated as empty. */
    function firstPopulated(part) {
      var candidates = part.split('~');
      for (var i = 0; i < candidates.length; i++) {
        var c = candidates[i].trim();
        if (c && c.indexOf('%') === -1) return c;
      }
      return '';
    }

    function parsePillText(text) {
      var product = '';
      var loc = text;
      var bar = text.indexOf('|');
      if (bar !== -1) {
        product = firstPopulated(text.slice(0, bar));
        loc = text.slice(bar + 1).trim();
      }
      var idx = loc.lastIndexOf(',');
      if (idx === -1) return null;
      return {
        product: product,
        city: loc.slice(0, idx).trim(),
        country: loc.slice(idx + 1).trim()
      };
    }

    var observer = new MutationObserver(function () {
      var text = (pill.textContent || '').trim();
      if (!text) return;
      var parsed = parsePillText(text);
      if (!parsed) return;
      gotInlineEvent = true;
      handleEvent(parsed.city, parsed.country, parsed.product);
    });
    observer.observe(pill, { childList: true, characterData: true, subtree: true });

    /* ---------- Popup mining: first-class parallel data source ----------
     * ProveSource's regular popup reliably shows product names and the
     * full event stream (including events the inline loop misses). We
     * watch for popup nodes from page load and mine them:
     *  - product: exact match of known product titles (from the Liquid
     *    map) inside the popup text — deterministic, no guessing
     *  - location: "City, Country" candidates validated against the
     *    coordinates table
     * Debounce in handleEvent prevents double-fires when the inline
     * embed reports the same event. Disable with
     * data-popup-fallback="false" on the .wm-section element.
     * ------------------------------------------------------------------ */
    var popupEnabled = section.dataset.popupFallback !== 'false';
    var popupObserver = null;

    /* Longest-first lists for text scanning: full titles and short names */
    var knownTitles = Object.keys(productNames).sort(function (a, b) {
      return b.length - a.length;
    });
    var knownShorts = [];
    (function () {
      var seen = {};
      for (var k in productNames) {
        var v = String(productNames[k]);
        var vl = v.toLowerCase();
        if (vl && !seen[vl]) { seen[vl] = true; knownShorts.push({ k: vl, v: v }); }
      }
      knownShorts.sort(function (a, b) { return b.k.length - a.k.length; });
    })();

    function findProductInText(text) {
      var lower = text.toLowerCase();
      var i;
      // 1. Known full catalog titles (map keys)
      for (i = 0; i < knownTitles.length; i++) {
        if (lower.indexOf(knownTitles[i]) !== -1) return knownTitles[i];
      }
      // 2. Known short display names (map values) — return original casing
      for (i = 0; i < knownShorts.length; i++) {
        if (lower.indexOf(knownShorts[i].k) !== -1) return knownShorts[i].v;
      }
      // 3. No map match: extract the raw product title directly from the
      //    popup sentence ("... purchased/bought <TITLE> 2 hours ago")
      var m = text.match(
        /(?:purchased|bought|ordered|got|grabbed)\s+(?:an?\s+|the\s+)?(.{2,70}?)(?=\s+(?:\d+\s+(?:second|minute|hour|day|week|month)s?\s+ago|recently|just now|yesterday|today)\b|[.!\n]|$)/i
      );
      if (m) return m[1].trim();
      return '';
    }

    function extractFromPopupText(text) {
      var product = findProductInText(text);
      // Try every "Something, Something" pair; the lookup table decides.
      var re = /([A-Za-z\u00C0-\u024F .'-]{2,40}),\s*([A-Za-z\u00C0-\u024F .'-]{2,40})/g;
      var m;
      while ((m = re.exec(text)) !== null) {
        var country = m[2].split(/\s{2,}|\n/)[0].trim().split(' ').slice(0, 3).join(' ');
        if (handleEvent(m[1].trim(), country, product)) return true;
        if (handleEvent(m[1].trim(), '', product)) return true;
      }
      return false;
    }

    function startPopupObserver() {
      if (popupObserver || !popupEnabled) return;
      popupObserver = new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
          var nodes = mutations[i].addedNodes;
          for (var j = 0; j < nodes.length; j++) {
            var node = nodes[j];
            if (node.nodeType !== 1) continue;
            var el = null;
            if (node.matches && node.matches('[class*="provesrc"], [id*="provesrc"]')) {
              el = node;
            } else if (node.querySelector) {
              el = node.querySelector('[class*="provesrc"], [id*="provesrc"]');
            }
            if (el && el !== pill && !section.contains(el)) {
              extractFromPopupText(el.innerText || '');
            }
          }
        }
      });
      popupObserver.observe(document.body, { childList: true, subtree: true });
    }
    startPopupObserver();

    /* Manual test hook — console: worldMapPing('Melbourne', 'AU', 'Magic Patch') */
    window.worldMapPing = function (city, country, product) {
      return handleEvent(city || '', country || '', product || '');
    };

    /* ---------- Idle auto-tour ---------- */
    var TOUR_DELAY = (parseFloat(section.dataset.tourDelay) || 8) * 1000;
    var TOUR_ENABLED = section.dataset.tourEnabled === 'true';
    var tourTimer = null;
    var tourStepTimer = null;
    var shipGroups = Array.prototype.slice.call(
      svg.querySelectorAll('.wm-country--active')
    );

    function markInteraction() {
      tourPause();
    }

    function tourStep() {
      if (pinnedGroup) return;
      var g = shipGroups[Math.floor(Math.random() * shipGroups.length)];
      focusCountry(g);
      var p = groupCenterTop(g);
      showTooltip(g, p.x, p.y);
      tourStepTimer = setTimeout(function () {
        focusCountry(null);
        hideTooltip();
        tourStepTimer = setTimeout(tourStep, 1200);
      }, 4000);
    }

    function tourPause() {
      clearTimeout(tourStepTimer);
      clearTimeout(tourTimer);
      if (!pinnedGroup) {
        focusCountry(null);
        hideTooltip();
      }
      if (TOUR_ENABLED && !reduceMotion) {
        tourTimer = setTimeout(tourStep, TOUR_DELAY);
      }
    }

    /* ---------- Counter ---------- */
    var counterEl = section.querySelector('[data-wm-counter]');
    var tickerLine = section.querySelector('[data-wm-ticker-line]');
    var base = counterEl ? parseInt(counterEl.dataset.base, 10) || 0 : 0;
    var tickMin = counterEl ? (parseInt(counterEl.dataset.tickMin, 10) || 20) * 1000 : 20000;
    var tickMax = counterEl ? (parseInt(counterEl.dataset.tickMax, 10) || 90) * 1000 : 90000;
    var suffix = counterEl ? (counterEl.dataset.suffix || '') : '';
    var current = base;

    function fmt(n) { return n.toLocaleString(); }

    function renderTicker() {
      if (tickerLine) tickerLine.textContent = fmt(current) + (suffix ? ' ' + suffix : '');
    }

    function countUp() {
      if (!tickerLine) return;
      if (reduceMotion) {
        renderTicker();
        scheduleTick();
        return;
      }
      var from = Math.floor(base * 0.85);
      var dur = 1500;
      var t0 = null;
      function step(ts) {
        if (!t0) t0 = ts;
        var k = Math.min((ts - t0) / dur, 1);
        var eased = 1 - Math.pow(1 - k, 3);
        current = Math.floor(from + (base - from) * eased);
        renderTicker();
        if (k < 1) {
          requestAnimationFrame(step);
        } else {
          current = base;
          renderTicker();
          scheduleTick();
        }
      }
      requestAnimationFrame(step);
    }

    function scheduleTick() {
      var wait = tickMin + Math.random() * (tickMax - tickMin);
      setTimeout(function () {
        current += 1;
        renderTicker();
        scheduleTick();
      }, wait);
    }

    /* ---------- Visibility gate ---------- */
    var started = false;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting && !started) {
          started = true;
          countUp();
          tourPause(); // arms the idle tour timer
          startStaleWatchdog();
          /* Default pill on load: show immediately so the map never looks
           * empty; the first real event replaces it. Not cached (replay
           * flag), so the replay pool stays 100% real orders. */
          if (lastFire === 0) {
            handleEvent(
              section.dataset.defaultCity || 'Melbourne',
              section.dataset.defaultCountry || 'AU',
              section.dataset.defaultProduct || '',
              true
            );
          }
          io.disconnect();
        }
      });
    }, { threshold: 0.3 });
    io.observe(section);

    /* ---------- Resize: reposition, never hide -----------------------
     * The pill stays visible until the next event replaces it. Mobile
     * browsers fire resize on scroll (URL bar collapse), so height-only
     * changes are ignored. ---------------------------------------------- */
    var resizeT;
    var lastWidth = window.innerWidth;
    window.addEventListener('resize', function () {
      if (window.innerWidth === lastWidth) return; // height-only: ignore
      lastWidth = window.innerWidth;
      clearTimeout(resizeT);
      resizeT = setTimeout(function () {
        if (lastVb) positionPill(lastVb);
        hideTooltip();
      }, 150);
    });
  }

  function boot() {
    document.querySelectorAll('.wm-section').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
